/**
 * 서비스 연동(라이브애드) — 연동 상태와 연동·해제 (261027-partner-link).
 * 소유자는 브랜드 설정에서 라이브애드 계정 이메일을 넣어 연동하고 해제한다. 운영자는 브랜드 상세에서 상태를 보고 해제한다.
 *
 * ★ 화면은 편의다 — 소유자 전용은 서버가 막는다(403).
 */
import { partner } from '@geo-admin/shared/contract/api/index.ts';
import { useState } from 'react';
import { Button } from './ui/button.tsx';
import { Card, CardBody, CardHeader } from './ui/card.tsx';
import { FieldError, Input } from './ui/form.tsx';
import { ErrorBox, Loading } from './ui/states.tsx';
import { useApi, useApiMutation } from '../lib/api.ts';
import { dateTime } from '../lib/format.ts';

export function PartnerCard({ brandId, admin = false }: { brandId: string; admin?: boolean }) {
  const view = useApi(admin ? partner.getAdminBrandPartner : partner.getMyBrandPartner, { params: { id: brandId } });
  const link = useApiMutation(partner.linkMyBrandPartner);
  const unlink = useApiMutation(admin ? partner.unlinkAdminBrandPartner : partner.unlinkMyBrandPartner);
  const [email, setEmail] = useState('');

  if (view.isLoading) return <Loading />;
  if (view.error) return <ErrorBox error={view.error} />;
  const v = view.data!;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    link.mutate({ params: { id: brandId }, body: { email: email.trim() } }, { onSuccess: () => setEmail('') });
  };

  return (
    <Card>
      <CardHeader
        title={`서비스 연동 — ${v.clientName}`}
        description={`${v.clientName} 계정 이메일을 넣으면 연동됩니다. 할인은 이 브랜드가 구독 중일 때만 적용됩니다`}
      />
      <CardBody className="grid gap-3 text-sm">
        {v.link ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-slate-700">
              연동됨{v.link.account ? ` · ${v.link.account}` : ''} · {dateTime(v.link.createdAt)}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={unlink.isPending}
              onClick={() =>
                confirm(`${v.clientName} 연동을 해제합니다. 그 계정의 할인이 끝납니다.`) &&
                unlink.mutate({ params: { id: brandId } })
              }
            >
              연동 해제
            </Button>
          </div>
        ) : admin ? (
          <span className="text-slate-600">아직 연동되지 않았습니다</span>
        ) : (
          <form className="flex flex-wrap gap-2" onSubmit={submit}>
            <Input
              type="email"
              placeholder={`${v.clientName} 계정 이메일`}
              className="min-w-0 flex-1"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Button type="submit" disabled={link.isPending || !email.trim()}>
              연동
            </Button>
          </form>
        )}
        {v.link && !admin ? (
          <p className="text-slate-500 text-xs">다른 계정이 연동됐다면 해제한 뒤 다시 연동해 주세요.</p>
        ) : null}
        <FieldError>{(link.error ?? unlink.error)?.message}</FieldError>
      </CardBody>
    </Card>
  );
}
